import { AloftPrice, isDefined, isUndefined } from '@aloft/toolkit-shared';

import { PropsWithChildren } from 'react';

import { PDFOnly } from '../../../../summary/components/PDFOnly.tsx';
import { Typography } from '../../../../summary/components/Typography.tsx';
import { isCosted } from '../../AdjustmentBreakdown/isCosted.ts';
import { SummarySection } from '../../SummarySection.tsx';
import { SummaryTableHorizontal } from '../../SummaryTableHorizontal.js';
import { TitleAndExportToggleLockup } from '../../TitleAndExportToggleLockup.tsx';
import {
  BreakdownCategory,
  BreakdownColumn,
  Tkv2PairedCostSummaryInterfaceV2,
} from '../../types.ts';
import { adjustment } from '../../utils/formatters/adjustment.ts';
import { percent } from '../../utils/formatters/percent.ts';
import { wholeDollar } from '../../utils/formatters/wholeDollar.ts';
import { getTableData } from '../../utils/getTableData.ts';

const getTableDef = (
  adjustmentType: Exclude<BreakdownCategory, 'allImprovements' | 'market'>,
): BreakdownColumn[] => [
  {
    header: 'SF | QL',
    columns: [
      { type: 'feature', adjustmentType, path: 'sqft' },
      { type: 'feature', adjustmentType, path: 'dcQuality' },
    ],
    combiner: ([sqft, finishedSqft, dcQuality]) => {
      if (isUndefined(sqft) && isUndefined(dcQuality)) {
        return '';
      }
      return `${isDefined(sqft) ? sqft : '-'} | ${isDefined(finishedSqft) ? finishedSqft : '-'}`;
    },
  },
  {
    header: 'Cost New',
    type: 'feature',
    adjustmentType,
    path: 'costNew',
    valueFormatter: wholeDollar,
  },
  {
    header: '- Dep',
    type: 'feature',
    adjustmentType,
    path: 'depreciationRatio',
    valueFormatter: percent,
  },
  {
    header: '= Dep Cost',
    type: 'feature',
    adjustmentType,
    path: 'depreciatedCost',
    valueFormatter: wholeDollar,
  },
  {
    header: '= Adjustment',
    type: 'adjustment',
    adjustmentType,
    path: 'adjustment',
    valueFormatter: adjustment,
  },
];

export const AdjustmentBreakdownAdditionalStructure = (
  props: PropsWithChildren<{
    summary: Tkv2PairedCostSummaryInterfaceV2;
    breakdownCategory: Exclude<BreakdownCategory, 'allImprovements' | 'market'>;
    compSequence: string[];
    id: string;
    title: string;
  }>,
) => {
  const { breakdownCategory, summary } = props;
  const commentaryProps = props.summary.exportableCommentaries[breakdownCategory];
  const { children, ...childProps } = props;

  if (!isCosted(summary.results, breakdownCategory)) {
    return null;
  }

  const { title } = props;

  const tableDef = getTableDef(breakdownCategory);
  const tableData = getTableData(tableDef, summary, summary.compSequence);

  const sortedPrices = Object.values(tableData)
    .map((row) => row[3])
    .filter(AloftPrice.isAloftPrice)
    .sort((a, b) => (a as AloftPrice).compare(b as AloftPrice));

  const minString = `${sortedPrices[0]?.toString(true)}`;
  const maxString = `${sortedPrices[sortedPrices.length - 1]?.toString(true)}`;
  const rangeString = [minString, maxString].join(' to ');

  return (
    <SummarySection marginBottom="2rem">
      <TitleAndExportToggleLockup {...childProps} commentary={commentaryProps} />
      {children ? (
        children
      ) : (
        <Typography variant="body1">
          {`${title} adjustments are based on the difference in depreciated cost between the subject and comparable. Depreciated cost ranged from ${rangeString}.`}
        </Typography>
      )}
      <PDFOnly>
        <Typography variant="body1">{commentaryProps.commentary ?? ''}</Typography>
      </PDFOnly>
      <SummaryTableHorizontal
        {...childProps}
        compSequence={childProps.compSequence}
        columns={tableDef}
        tableData={tableData}
        subjectHeader="SUB"
        compHeaderPrefix="CP"
      />
    </SummarySection>
  );
};
